import { useUrlContext } from '@msdk/common'
import { FormNode, traverseSiblings } from '@msdk/definitions'
import { useEffect, useRef, useState } from 'react'

export function getVisibleTabs(node: FormNode): FormNode[] {
  const visibleTabs: FormNode[] = []
  traverseSiblings(node, (sibling) => {
    if (
      (sibling.value.type === 'Tab' || sibling.value.type === 'CustomTab') &&
      sibling.value.visibility === 'Visible'
    ) {
      visibleTabs.push(sibling)
    }
  })
  return visibleTabs
}

function getFormTabs(node: FormNode | undefined): FormNode[] {
  if (!node) {
    return []
  }

  if (node.value.type === 'Group') {
    return getFormTabs(node.firstChild)
  }

  if (node.value.type !== 'Tab' && node.value.type !== 'CustomTab') {
    return []
  }

  return getVisibleTabs(node)
}

export function useTabs({ form }: { form: FormNode | undefined }) {
  const context = useUrlContext()
  const visibleTabs = getFormTabs(form)
  const mapping = context.context.ftm?.split(':')
  const mappedTab =
    mapping?.length === 2 && mapping[0] === form?.value.rootFormModelName
      ? visibleTabs.findIndex((tab) => tab.id === mapping[1])
      : -1
  const startingTab = mappedTab >= 0 ? mappedTab : context.context.ft ? parseInt(context.context.ft) : 0
  const [selectedTab, setSelectedTab] = useState<number>(startingTab)
  const initialized = useRef(!!form)

  // Tab IDs can only be resolved once the asynchronously loaded form exists.
  useEffect(() => {
    if (initialized.current || !form) {
      return
    }

    initialized.current = true
    setSelectedTab(startingTab)
  }, [form, startingTab])

  const visibleTabsCount = visibleTabs.length

  function moveToTab(tabIndex: number): void {
    if (tabIndex >= 0 && tabIndex < visibleTabsCount) {
      setSelectedTab(tabIndex)
    }
  }

  function isFirstTab(): boolean {
    return selectedTab === 0
  }

  function isLastTab(): boolean {
    return selectedTab >= visibleTabsCount - 1
  }

  function updateSelectedTab(newSelectedTab: number): void {
    setSelectedTab(newSelectedTab)
  }

  return {
    selectedTab,
    isFirstTab,
    isLastTab,
    updateSelectedTab,
    startingTab,
    moveToTab,
  }
}
